/**
 * McpConnectModal — „jak se připojím k ACM přes MCP".
 *
 * Stejný průvodce na třech úrovních: celé ACM, Codable hub, jeden projekt.
 * One-click IDE (VS Code, VSCodium, Antigravity, Cursor) + pouze otevřít /
 * pouze stáhnout. Token nese identitu vydavatele, ne LLM; volitelný popis
 * klíče je jen pro upamatování. Backend: acm_self_connect / codable.token_*.
 *
 * Projektový režim (`projectSlug` prop vyplněný): stejný modal, ale token
 * i výpis/revoke jdou přes `codable.token_create` / `codable.token_list` /
 * `codable.token_revoke` — scope klampnutý na `codable:project:<slug>`, ne
 * na celoplatformní oprávnění přihlášeného uživatele. Používá se z
 * `CodableWorkspace.tsx` (button „Připojit IDE (Cursor průvodce)" uvnitř
 * konkrétního projektu). `CodableList.tsx` a `AppLayout.tsx` volají modal
 * bez `projectSlug` → chování zůstává celoplatformní, beze změny.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, Check, Copy, Globe, KeyRound, Plus, Plug, Trash2, X } from 'lucide-react';
import { api } from '../lib/api';
import { isLegacyProjectId, parseProjectScope } from '../lib/project-id';
import {
  IDE_META,
  buildIdePack,
  downloadTextFile,
  type IdeClient,
  type IdePack,
  type TokenClientType,
} from '../lib/ide-onboarding';
import { Badge, Button, Select } from './ui';
import { IdeOneClickOnboarding } from './IdeOneClickOnboarding';
import { themedConfirm } from '../lib/themed-dialogs';

/** UI origin a MCP ingress jsou oddělené. URL vždy vydává backend resolver. */
function browserMcpUrl(resolved?: string): string {
  return resolved ?? '';
}

/** Interior = http nebo host je holá IP → self-signed cert, jinak veřejná doména. */
function isInteriorUrl(u: string): boolean {
  try {
    const { protocol, hostname } = new URL(u);
    return protocol !== 'https:' || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
  } catch {
    return true;
  }
}

function localizeOnboardingDoc(
  pack: IdePack,
  mcpUrl: string,
  key: string,
  t: (k: string, d: string, o?: Record<string, unknown>) => string,
  protocolNote?: string,
  projectSlug?: string | null,
  projectId?: string | null,
  codableModule?: boolean,
): string {
  const when = new Date().toISOString().slice(0, 16).replace('T', ' ');
  const meta = IDE_META[pack.ide];
  if (codableModule && !projectSlug) {
    return t(
      'connect.onboardingDocCodable',
      `# Codable — {{ideLabel}}

Vygenerováno {{when}}. Klíč platí pro Codable na tomto uzlu (projekty, ke kterým máš právo).

## Nejrychlejší cesta
    {{deeplink}}

## MCP
- **URL:** {{mcpUrl}}
- **API klíč:** \`{{key}}\`

## Co agent smí
- \`codable.<akce>\` se slug (např. codable.project_list, codable.files)
- u konkrétního projektu \`PRJ-….codable.*\` / \`PRJ-….orchestrator.*\` podle tools/list

Nesmí platformní admin ani cizí projekty.

## Jak zadávat práci
1. Seznam projektů → otevři jeden
2. Strom / hledání → čtení → zápis → status → commit
3. Cesty relativně ke kořeni repa

Vyzkoušej: „vypiš moje Codable projekty“.

## Ruční konfigurace ({{configPath}})
\`\`\`json
{{snippet}}
\`\`\`

## Správa
Codable → nastavení → Připojit IDE → Revoke.
`,
      {
        ideLabel: meta.label,
        when,
        deeplink: pack.deeplink,
        mcpUrl,
        key,
        snippet: pack.snippet,
        configPath: meta.configPath,
      },
    );
  }
  if (projectSlug) {
    const prjPrefix = projectId ? `${projectId}.` : '';
    const toolHint = projectId ? `${projectId}.codable.files` : `codable.files (slug ${projectSlug})`;
    return t(
      'connect.onboardingDocProject',
      `# Codable — {{ideLabel}} · {{slug}}

Vygenerováno {{when}}. Klíč platí jen pro tento projekt.

## Nejrychlejší cesta
Otevři odkaz — {{ideLabel}} se spustí a MCP si vyplní sám:

    {{deeplink}}

## MCP
- **URL:** {{mcpUrl}}
- **API klíč:** \`{{key}}\` (plaintext ACM znovu neukáže)

## Co agent smí
Jen tento projekt:
- \`{{toolHint}}\` a další \`{{prjPrefix}}codable.*\`
- lifecycle: \`{{prjPrefix}}orchestrator.*\`

Nesmí platformní admin, cizí projekty, ani volání bez prefixu projektu.

## Jak zadávat práci
1. Strom / hledání → čtení → zápis → status → commit
2. Cesty relativně ke kořeni repa (ne \`src/src\`)
3. Binárky: encoding=base64

Vyzkoušej: „vypiš soubory projektu“ nebo „zavolej {{toolHint}}“.

## Ruční konfigurace ({{configPath}})
\`\`\`json
{{snippet}}
\`\`\`

## Grok / Claude konektor
Do konektoru patří MCP adresa výše. OAuth si najde na \`/mcp/oauth\`. Tento API klíč je pro IDE.

## Správa
V projektu → Připojit IDE → Revoke.
`,
      {
        ideLabel: meta.label,
        slug: projectSlug,
        when,
        deeplink: pack.deeplink,
        mcpUrl,
        key,
        snippet: pack.snippet,
        configPath: meta.configPath,
        toolHint,
        prjPrefix,
      },
    );
  }
  return t(
    'connect.onboardingDoc',
    `# ACM — {{ideLabel}} přes MCP

Vygenerováno {{when}} v ACM dashboardu.

## Nejrychlejší cesta
Otevři tento odkaz ({{ideLabel}} se spustí a konfiguraci vloží sám):

    {{deeplink}}

## Ruční konfigurace ({{configPath}})
\`\`\`json
{{snippet}}
\`\`\`

## Údaje
- **MCP endpoint:** {{mcpUrl}}
- **API klíč:** \`{{key}}\`
  (nese tvoje oprávnění — zacházej s ním jako s heslem; plaintext se v ACM už znovu nezobrazí)
{{protocolBlock}}
## Kroky
1. Otevři deep-link výše, NEBO vlož JSON blok do \`{{configPath}}\` a {{ideLabel}} restartuj.
2. MCP: server má svítit jako připojený.
3. V chatu vyzkoušej: „zavolej acm tool toolbox.health_status".

## Správa
Token jde kdykoli zrušit v ACM: profil → IDE / seznam tokenů → Revoke.
`,
    {
      when,
      deeplink: pack.deeplink,
      snippet: pack.snippet,
      mcpUrl,
      key,
      ideLabel: meta.label,
      configPath: meta.configPath,
      protocolBlock: protocolNote
        ? t(
          'connect.onboardingProtocol',
          '\n## Protokol nástrojů projektu\n{{note}}\n',
          { note: protocolNote },
        )
        : '',
    },
  );
}

/** Vytáhne viditelné Project ID ze scopes. Historické PRJ-XXXXXXXX se neukazuje. */
function extractProjectId(scopes: string[]): string | null {
  for (const s of scopes) {
    const parsed = parseProjectScope(s);
    if (parsed && !isLegacyProjectId(parsed.project_id)) return parsed.project_id;
  }
  return null;
}

interface TokenRow {
  id: string;
  label: string;
  key_preview: string;
  enabled: boolean;
  expires_at: string | null;
  last_used: string | null;
  use_count: number;
  created_at: string;
  /** Codable projekt, na který je token scoped (null = celé ACM dle scopes). */
  project?: string | null;
}

interface InfoResp {
  ok: boolean;
  mcp_url?: string;
  mcp_url_private?: string;
  /** Obě adresy téhož MCP endpointu — UI z nich staví roletu. */
  endpoints?: Array<{ kind: string; url: string; published: boolean }>;
  published?: boolean;
  scopes?: string[];
  tokens?: TokenRow[];
}

interface McpPublicationState {
  domain: string;
  url: string | null;
  desired: boolean;
  actual: boolean;
  published: boolean;
}

interface McpEndpoint {
  kind: 'project' | 'project-pending' | 'project-disabled';
  url: string | null;
  requested_url: string;
  enabled: boolean;
  project: McpPublicationState;
}

interface ProjectTokensResp {
  ok: boolean;
  error?: string;
  slug?: string;
  mcp_url?: string;
  mcp_endpoint?: McpEndpoint;
  mcp_ready?: boolean;
  oauth_platform_enabled?: boolean;
  oauth_enabled?: boolean;
  tokens?: Array<{
    id: string;
    label: string;
    key_preview: string;
    client_type: string;
    enabled: boolean;
    expires_at: string | null;
    last_used: string | null;
    use_count: number;
    created_at: string;
  }>;
}

interface CreateResp {
  ok: boolean;
  error?: string;
  plaintext?: string;
  snippet?: string;
  mcp_url?: string;
  mcp_endpoint?: McpEndpoint;
  scopes?: string[];
}

interface Props {
  onClose?: () => void;
  /** Vyplněno = projektový režim (token scoped na jeden Codable projekt). */
  projectSlug?: string | null;
  /** Orchestrator PRJ-ID projektu — potřebné jen pro tlačítko publikace vlastní domény. */
  orchestratorProjectId?: string | null;
  /** Telefonní záložka: bez overlay, obsah se kreslí na místě. */
  embedded?: boolean;
  /** Modulové napojení (Codable hub) — stejné MCP, užší klíč. */
  module?: 'codable';
}

export default function McpConnectModal({
  onClose,
  projectSlug,
  orchestratorProjectId,
  embedded = false,
  module: connectModule,
}: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isProject = !!projectSlug;
  const isCodable = connectModule === 'codable' && !isProject;
  const serverName = isProject && projectSlug
    ? `codable-${projectSlug}`
    : isCodable
    ? 'codable'
    : 'acm';
  /** Kterou adresu téhož MCP endpointu použít — veřejnou, nebo vnitřní adresu uzlu. */
  const [endpointKind, setEndpointKind] = useState<'public' | 'private'>('public');
  const [label, setLabel] = useState('');
  const [expiresDays, setExpiresDays] = useState<number | null>(null);
  const [newToken, setNewToken] = useState<
    null | { plaintext: string; snippet: string; mcp_url: string; scopes: string[]; protocolNote?: string | null }
  >(null);
  const [idePack, setIdePack] = useState<IdePack | null>(null);

  // Info (scopes přihlášeného) — jen celoplatformní režim, projektový režim
  // dostane endpoint z codable.token_list.
  const infoQ = useQuery({
    queryKey: ['acm-connect-info', isCodable ? 'codable' : 'platform'],
    queryFn: () =>
      api.mcpSession<InfoResp>('acm-self.connect', {
        action: 'info',
        client_type: 'generic',
        ...(isCodable ? { module: 'codable' } : {}),
      }),
    enabled: !isProject,
  });

  // Tokeny — dva samostatné dotazy (žádné podmíněné volání hooku), aktivní je
  // jen jeden dle režimu. Query key obsahuje projectSlug, aby se cache nikdy
  // nemíchala mezi projekty.
  const platformTokensQ = useQuery({
    queryKey: ['acm-connect-tokens', isCodable ? 'codable' : 'platform'],
    queryFn: () =>
      api.mcpSession<InfoResp>('acm-self.connect', {
        action: 'list',
        ...(isCodable ? { module: 'codable' } : {}),
      }),
    enabled: !isProject,
  });
  const projectTokensQ = useQuery({
    queryKey: ['acm-connect-tokens', 'project', projectSlug ?? null],
    queryFn: () => api.mcpSession<ProjectTokensResp>('codable.token_list', { slug: projectSlug }),
    enabled: isProject,
  });
  const tokensLoading = isProject ? projectTokensQ.isLoading : platformTokensQ.isLoading;
  const invalidateTokens = () =>
    qc.invalidateQueries({
      queryKey: isProject
        ? ['acm-connect-tokens', 'project', projectSlug ?? null]
        : ['acm-connect-tokens', isCodable ? 'codable' : 'platform'],
    });

  type CreateArgs = { clientType: TokenClientType; keyLabel: string };

  // ── Create token ─────────────────────────────────────────────────────────
  const createPlatformM = useMutation({
    mutationFn: ({ clientType, keyLabel }: CreateArgs) =>
      api.mcpSession<CreateResp>('acm-self.connect', {
        action: 'create',
        client_type: clientType,
        label: keyLabel || undefined,
        expires_days: expiresDays ?? undefined,
        endpoint: endpointKind,
        ...(isCodable ? { module: 'codable' } : {}),
      }),
    onError: (e: unknown) => toast.error(String((e as Error).message)),
  });

  const createProjectM = useMutation({
    mutationFn: ({ clientType, keyLabel }: CreateArgs) =>
      api.mcp<CreateResp>('codable.token_create', {
        slug: projectSlug,
        client_type: clientType,
        label: keyLabel || undefined,
        expires_days: expiresDays ?? undefined,
      }),
    onError: (e: unknown) => toast.error(String((e as Error).message)),
  });
  const createM = isProject ? createProjectM : createPlatformM;

  // ── Revoke ───────────────────────────────────────────────────────────────
  const revokePlatformM = useMutation({
    mutationFn: (key_id: string) =>
      api.mcpSession<{ ok: boolean; error?: string }>('acm-self.connect', { action: 'revoke', key_id }),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t('connect.revokeFail', 'Chyba revoke'));
        return;
      }
      toast.success(t('connect.tokenRevoked', 'Token zrušen'));
      invalidateTokens();
    },
  });
  const revokeProjectM = useMutation({
    mutationFn: (key_id: string) =>
      api.mcp<{ ok: boolean; error?: string }>('codable.token_revoke', { key_id }),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t('connect.revokeFail', 'Chyba revoke'));
        return;
      }
      toast.success(t('connect.tokenRevoked', 'Token zrušen'));
      invalidateTokens();
    },
  });
  const revokeM = isProject ? revokeProjectM : revokePlatformM;

  // ── Publikace vlastní projektové domény ─────────────────────────────────
  // Reálná implementace tohoto tlačítka: `<PRJ-ID>.orchestrator.mcp-publish`
  // (stejná qualified akce jako v ConnectIdeModal.tsx) → `acm-self.endpoint_alias`
  // → `network.publish` (jediný publikační flow, viz orch_project_action.ts +
  // acm_self_endpoint_alias.ts). Přímé volání `network.publish` odsud by
  // znamenalo v UI znovu sestavit backend-privátní nginx `advanced_config`
  // (proxy hlavičky pro projektové MCP routing) a vyžadovalo by scope
  // `network:write`, který projektový self-service tok nepředpokládá — proto
  // se použije existující qualified orchestrator akce, která network.publish
  // spustí interně se správnou konfigurací.
  const publishProjectM = useMutation({
    mutationFn: () => {
      if (!orchestratorProjectId) {
        throw new Error(t('connect.publishNeedsProjectId', 'Chybí orchestrator project ID'));
      }
      return api.mcp<{ ok: boolean; error?: string; published?: boolean; mcp_url?: string; requested_url?: string }>(
        `${orchestratorProjectId}.orchestrator.mcp-publish`,
        {},
      );
    },
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t('connect.publishFail', 'Publikace projektové adresy selhala'));
        return;
      }
      toast.success(
        r.published
          ? t('connect.publishSuccess', { url: r.mcp_url ?? '', defaultValue: `Projektová MCP adresa publikována: ${r.mcp_url ?? ''}` })
          : t('connect.publishPending', 'Publikace vyžádána, čeká se na proxy a certifikát.'),
      );
      invalidateTokens();
    },
    onError: (e: unknown) => toast.error(String((e as Error).message)),
  });

  const oauthMut = useMutation({
    mutationFn: (enabled: boolean) => {
      if (!orchestratorProjectId) {
        throw new Error(t('connect.publishNeedsProjectId', 'Chybí orchestrator project ID'));
      }
      return api.mcp<{ ok: boolean; error?: string; oauth_enabled?: boolean }>(
        `${orchestratorProjectId}.orchestrator.${enabled ? 'oauth-publish' : 'oauth-unpublish'}`,
        {},
      );
    },
    onSuccess: (r, enabled) => {
      if (!r.ok) {
        toast.error(r.error ?? t('connect.grokOauthFail', 'Změna OAuth selhala'));
        return;
      }
      toast.success(
        enabled
          ? t('connect.grokOauthOn', 'OAuth u projektu je zapnuté')
          : t('connect.grokOauthOff', 'OAuth u projektu je vypnuté'),
      );
      invalidateTokens();
    },
    onError: (e: unknown) => toast.error(String((e as Error).message)),
  });

  const [copiedId, setCopiedId] = useState<string | null>(null);
  const copiedTimer = useRef<number>(0);
  useEffect(() => () => window.clearTimeout(copiedTimer.current), []);
  const copy = (id: string, v: string) => {
    navigator.clipboard.writeText(v).then(() => {
      setCopiedId(id);
      window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopiedId(null), 2200);
    }).catch(() => toast.error(t('common.copyFailed', 'Kopírování selhalo')));
  };
  const copyLabel = (id: string, idle: string) =>
    copiedId === id ? t('ui.copied', 'Zkopírováno') : idle;

  // ── Odvozené hodnoty pro render ──────────────────────────────────────────
  const platformMcpUrl = browserMcpUrl(infoQ.data?.mcp_url);
  const projectEndpoint = projectTokensQ.data?.mcp_endpoint;
  const projectMcpUrl = projectTokensQ.data?.mcp_url ?? projectEndpoint?.url ?? '';
  // Roleta veřejná / privátní adresa téhož MCP endpointu. Nabídne se jen když
  // jsi přišel privátní adresou — venku je volba „privátní" k ničemu
  // (Filip 2026-08-17). Privátní adresa uzlu má cert z interní CA.
  const onPrivateAddress = isInteriorUrl(globalThis.location?.origin ?? '');
  const publicEp = infoQ.data?.endpoints?.find((e) => e.kind === 'public');
  const privateEp = infoQ.data?.endpoints?.find((e) => e.kind === 'private');
  const showEndpointPicker = !isProject && onPrivateAddress && !!publicEp && !!privateEp;
  const platformSelectedUrl = endpointKind === 'private' && privateEp
    ? privateEp.url
    : publicEp?.url ?? privateEp?.url ?? platformMcpUrl;
  const mcpUrl = isProject ? projectMcpUrl : platformSelectedUrl;
  const interior = isInteriorUrl(mcpUrl);
  const scopes = infoQ.data?.scopes ?? [];
  const projectDomainPublished = projectEndpoint?.kind === 'project' &&
    projectTokensQ.data?.mcp_ready === true;

  // Aktivní nahoře, v rámci skupiny naposledy použité první — ať se v tom
  // uživatel neztratí (Filip 2026-07-24).
  const rawTokens: TokenRow[] = isProject
    ? (projectTokensQ.data?.tokens ?? []).map((tk) => ({
      id: tk.id,
      label: tk.label,
      key_preview: tk.key_preview,
      enabled: tk.enabled,
      expires_at: tk.expires_at,
      last_used: tk.last_used,
      use_count: tk.use_count,
      created_at: tk.created_at,
      project: null,
    }))
    : platformTokensQ.data?.tokens ?? [];
  const tokens = [...rawTokens].sort((a, b) => {
    if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
    return (b.last_used ?? '').localeCompare(a.last_used ?? '');
  });
  // Veřejná adresa nodu (publikovaný přes acm-self) — pro IDE config má přednost
  // před privátní URL/IP, na které je zrovna otevřený prohlížeč. Jen celoplatformní režim.
  const cursorPublicUrl = infoQ.data?.published && infoQ.data?.mcp_url && !isInteriorUrl(infoQ.data.mcp_url)
    ? infoQ.data.mcp_url
    : null;

  const connectReady = isProject
    ? projectDomainPublished
    : infoQ.data?.published !== false;

  const packFor = (
    ide: IdeClient,
    plaintext: string,
    url: string,
    protocolNote?: string | null,
  ): IdePack => {
    const base = buildIdePack(ide, url, plaintext, {
      serverName,
      projectSlug,
      protocolNote: protocolNote ?? undefined,
    });
    return {
      ...base,
      doc: localizeOnboardingDoc(
        base,
        url,
        plaintext,
        t as never,
        protocolNote ?? undefined,
        projectSlug,
        orchestratorProjectId,
        isCodable,
      ),
    };
  };

  const applyFreshToken = (
    plaintext: string,
    url: string,
    scopes: string[],
    ide: IdeClient,
    protocolNote?: string | null,
  ) => {
    const pack = packFor(ide, plaintext, url, protocolNote);
    setNewToken({
      plaintext,
      snippet: pack.snippet,
      mcp_url: url,
      scopes,
      protocolNote: protocolNote ?? null,
    });
    setIdePack(pack);
    return pack;
  };

  const mintToken = async (ide: IdeClient) => {
    if (newToken) {
      return {
        plaintext: newToken.plaintext,
        url: newToken.mcp_url,
        scopes: newToken.scopes,
        protocolNote: newToken.protocolNote,
      };
    }
    const keyLabel = label.trim() || IDE_META[ide].defaultLabel;
    const r = await createM.mutateAsync({ clientType: ide, keyLabel });
    if (!r.ok || !r.plaintext) {
      toast.error(r.error ?? t('connect.createError', 'Chyba tvorby tokenu'));
      return null;
    }
    const url = isProject
      ? (r.mcp_url ?? '')
      : (isInteriorUrl(browserMcpUrl(r.mcp_url)) && cursorPublicUrl
        ? cursorPublicUrl
        : browserMcpUrl(r.mcp_url));
    const prj = extractProjectId(r.scopes ?? []);
    const protocolNote = prj ? t('connect.projectProtocolNote', { prj }) : null;
    invalidateTokens();
    return { plaintext: r.plaintext, url, scopes: r.scopes ?? [], protocolNote };
  };

  const onConnectIde = async (ide: IdeClient) => {
    const minted = await mintToken(ide);
    if (!minted) return;
    const pack = applyFreshToken(
      minted.plaintext,
      minted.url,
      minted.scopes,
      ide,
      minted.protocolNote,
    );
    if (isProject || isCodable) downloadTextFile(pack.filename, pack.doc);
    window.location.href = pack.deeplink;
  };

  const onOpenOnly = () => {
    if (!idePack) return;
    window.location.href = idePack.deeplink;
  };

  const onDownloadOnly = () => {
    if (!idePack) return;
    downloadTextFile(idePack.filename, idePack.doc);
  };

  const onCreateManual = async () => {
    const r = await createM.mutateAsync({
      clientType: 'generic',
      keyLabel: label.trim() || t('connect.manualKeyLabel', 'osobní token'),
    });
    if (!r.ok || !r.plaintext) {
      toast.error(r.error ?? t('connect.createError', 'Chyba tvorby tokenu'));
      return;
    }
    const url = isProject ? (r.mcp_url ?? '') : browserMcpUrl(r.mcp_url);
    const prj = extractProjectId(r.scopes ?? []);
    const protocolNote = prj ? t('connect.projectProtocolNote', { prj }) : null;
    applyFreshToken(r.plaintext, url, r.scopes ?? [], idePack?.ide ?? 'vscode', protocolNote);
    invalidateTokens();
  };

  const body = (
      <div
        className={embedded
          ? 'space-y-5'
          : 'acm-glass-strong rounded-2xl p-6 max-w-3xl w-full max-h-[85vh] overflow-y-auto space-y-5 animate-fade-in'}
        onClick={embedded ? undefined : (e) => e.stopPropagation()}
      >
        <div className='flex items-center justify-between'>
          <h3 className={`font-bold text-gray-100 flex items-center gap-2 ${embedded ? 'text-base' : 'text-lg'}`}>
            <Plug size={20} className='text-plex' />
            {isProject
              ? t('connect.projectTitle', { slug: projectSlug, defaultValue: `Připojit projekt ${projectSlug} přes MCP` })
              : isCodable
              ? t('connect.codableTitle', 'Připojit Codable přes MCP')
              : t('connect.title', 'Připojit se k ACM přes MCP')}
          </h3>
          {onClose && !embedded && (
            <button
              type='button'
              onClick={onClose}
              className='rounded-lg p-1.5 text-gray-500 hover:bg-gray-800/60 hover:text-gray-100 transition-colors'
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* Bez PUBLIKOVANÉ MCP URL není kam se napojit: vnitřní adresa uzlu
            (self-signed cert z interní CA) je z počítače uživatele nebo
            zákaznického admina nedostupná. Napojení se proto nenabízí a řekne
            se PROČ a co udělat (Filip 2026-08-17). */}
        {!isProject && infoQ.data && infoQ.data.published === false && (
          <div className='bg-amber-500/5 border border-amber-500/30 rounded-lg p-3 space-y-1'>
            <div className='text-sm font-semibold text-amber-300'>
              {t('connect.mcpUnpublishedTitle', 'MCP endpoint není publikovaný')}
            </div>
            <div className='text-xs text-gray-400 leading-relaxed'>
              {t(
                'connect.mcpUnpublishedBody',
                'Napojení nemá kam mířit — vnitřní adresa uzlu není z tvého počítače dostupná a má certifikát z interní CA. Zveřejni MCP ve Veřejném přístupu a zkus to znovu.',
              )}
            </div>
            <div className='text-xs text-amber-200/80'>
              {t('connect.mcpUnpublishedAction', 'Veřejný přístup → služby ACM → MCP → zapnout')}
            </div>
          </div>
        )}

        <div className='bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 text-xs text-gray-400 leading-relaxed'>
          {isProject
            ? t('connect.projectIntro', {
              slug: projectSlug,
              defaultValue:
                `Token platí jen pro projekt ${projectSlug} (scope codable:project:${projectSlug}). Nástroje se volají s přesným prefixem projektu, ne obecně codable.*.`,
            })
            : isCodable
            ? t(
              'connect.codableIntro',
              'Klíč platí pro Codable na tomto uzlu — projekty, ke kterým máš právo. Nástroje: codable.<akce> se slug, nebo PRJ-….codable.* podle tools/list. Ne platformní admin.',
            )
            : (
              <>
                {t(
                  'connect.intro',
                  'Napoj IDE na celé ACM. Token nese tvoje oprávnění — uvidíš přesně ty nástroje, na které máš scope (Codable, orchestrátor, …). Jednotlivý Codable projekt jde připojit i samostatnou URL zevnitř projektu (Workspace → Připojit IDE).',
                )}{' '}
                {t(
                  'connect.platformScopeNote',
                  'Token dosáhne na projekty podle tvého oprávnění; projektové nástroje se volají jako codable.<akce> se slug parametrem.',
                )}
              </>
            )}
        </div>

        {isProject && (
          <div className='text-[11px] text-gray-500 -mt-3 px-1'>
            {t('connect.projectScopeHint', 'Scope tokenu:')}{' '}
            <code className='font-mono text-blue-300'>codable:project:{projectSlug}</code>
          </div>
        )}

        {isProject && (
          <div className='border border-emerald-500/30 bg-emerald-500/5 rounded-lg p-3 space-y-2'>
            <div className='text-sm font-semibold text-emerald-200'>
              {t('connect.grokOauthTitle', 'Grok / Claude — OAuth (SSO)')}
            </div>
            {!mcpUrl
              ? (
                <p className='text-xs text-gray-400'>
                  {t('connect.grokOauthNeedMcp', 'Nejdřív zapni MCP u tohoto projektu.')}
                </p>
              )
              : projectTokensQ.data?.oauth_platform_enabled !== true
              ? (
                <p className='text-xs text-gray-400'>
                  {t(
                    'connect.grokOauthNeedPlatform',
                    'SSO ještě nepovolilo MCP OAuth modulům. Zapnutí je v SSO → OAuth.',
                  )}
                </p>
              )
              : projectTokensQ.data?.oauth_enabled === true
              ? (
                <>
                  <p className='text-xs text-gray-400 leading-relaxed'>
                    {t(
                      'connect.grokOauthBody',
                      'Do Groku vlož jen MCP adresu. Přihlášení jde na SSO, API klíč nepotřebuješ. Cursor dál používá klíč níže.',
                    )}
                  </p>
                  <div className='flex items-center gap-2'>
                    <code className='flex-1 text-[11px] font-mono text-gray-200 break-all'>
                      {mcpUrl}
                    </code>
                    <Button
                      variant='secondary'
                      size='sm'
                      icon={copiedId === 'grok-mcp' ? <Check size={12} /> : <Copy size={12} />}
                      onClick={() => copy('grok-mcp', mcpUrl)}
                    >
                      {copyLabel('grok-mcp', t('connect.copy', 'Zkopírovat'))}
                    </Button>
                  </div>
                  {orchestratorProjectId && (
                    <Button
                      variant='ghost'
                      size='sm'
                      loading={oauthMut.isPending}
                      onClick={() => oauthMut.mutate(false)}
                    >
                      {t('connect.grokOauthDisable', 'Vypnout OAuth')}
                    </Button>
                  )}
                </>
              )
              : (
                <>
                  <p className='text-xs text-gray-400'>
                    {t(
                      'connect.grokOauthEnableHint',
                      'Zapne OAuth jen u tohoto Codable projektu.',
                    )}
                  </p>
                  <Button
                    variant='primary'
                    size='sm'
                    disabled={!orchestratorProjectId || !projectDomainPublished}
                    loading={oauthMut.isPending}
                    onClick={() => oauthMut.mutate(true)}
                  >
                    {t('connect.grokOauthEnable', 'Zapnout OAuth')}
                  </Button>
                </>
              )}
          </div>
        )}

        <IdeOneClickOnboarding
          disabled={!connectReady}
          creating={createM.isPending}
          label={label}
          onLabelChange={setLabel}
          onConnect={(ide) => void onConnectIde(ide)}
          canOpenOrDownload={!!idePack}
          activeIde={idePack?.ide ?? null}
          onOpenOnly={onOpenOnly}
          onDownloadOnly={onDownloadOnly}
          title={isProject || isCodable
            ? t('connect.quickTitleProject', 'One-click do IDE')
            : undefined}
          hint={isProject
            ? t(
              'connect.quickHintProject',
              'VS Code, VSCodium, Antigravity nebo Cursor: vytvoří klíč jen pro tento projekt, stáhne Codable návod a otevře IDE s MCP. Grok/Claude konektor klíč nepotřebuje — stačí MCP adresa.',
            )
            : isCodable
            ? t(
              'connect.quickHintCodable',
              'VS Code, VSCodium, Antigravity nebo Cursor: klíč jen na Codable (všechny tvoje projekty), stáhne návod a otevře IDE. Jednotlivý projekt má vlastní napojení ve workspace.',
            )
            : undefined}
        />
        {!isProject && interior && cursorPublicUrl && (
          <div className='text-[11px] text-blue-300 -mt-3 px-1 flex items-start gap-1.5'>
            <AlertTriangle size={12} className='shrink-0 mt-0.5' />
            {t('connect.quickPublicUsed', 'Jsi na privátní adrese — do configu pro Cursor se použije veřejná adresa:')}{' '}
            <code className='font-mono break-all'>{cursorPublicUrl}</code>
          </div>
        )}

        {/* MCP endpoint */}
        <div>
          <div className='text-xs text-gray-500 mb-1'>
            {isProject ? t('connect.projectEndpoint', 'MCP endpoint projektu') : t('connect.endpoint', 'MCP endpoint ACM')}
          </div>
          {showEndpointPicker && (
            <select
              value={endpointKind}
              onChange={(e) => setEndpointKind(e.target.value as 'public' | 'private')}
              className='mb-2 w-full bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-xs text-gray-200'
            >
              <option value='public'>
                {t('connect.endpointPublic', 'Veřejná adresa')} — {publicEp?.url}
              </option>
              <option value='private'>
                {t('connect.endpointPrivate', 'Privátní adresa uzlu')} — {privateEp?.url}
              </option>
            </select>
          )}
          <div className='flex items-center gap-2 flex-wrap'>
            <code
              className={`flex-1 min-w-0 bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-xs font-mono break-all ${
                'text-emerald-300'
              }`}
            >
              {mcpUrl}
            </code>
            <Button
              size='sm'
              variant='ghost'
              icon={copiedId === 'mcp' ? <Check size={14} /> : <Copy size={14} />}
              onClick={() => copy('mcp', mcpUrl)}
            >
              {copiedId === 'mcp' ? t('ui.copied', 'Zkopírováno') : null}
            </Button>
            {mcpUrl && !interior && (
              <a
                href={mcpUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex items-center gap-1 text-xs text-plex hover:underline px-1'
              >
                <Globe size={12} />
                {t('servers.openPublic', 'Otevřít ve Safari / prohlížeči')}
              </a>
            )}
          </div>
          {!isProject && interior && cursorPublicUrl && !showEndpointPicker && (
            <div className='mt-2 rounded-lg border border-emerald-500/25 bg-emerald-500/5 px-2.5 py-2 space-y-1'>
              <div className='text-[10px] uppercase tracking-wider text-emerald-400 font-semibold'>
                {t('servers.publicMcp', 'Veřejná URL (MCP / DEV)')}
              </div>
              <code className='block text-[11px] font-mono text-emerald-200 break-all'>{cursorPublicUrl}</code>
              <div className='flex flex-wrap gap-3 pt-0.5'>
                <a
                  href={cursorPublicUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='inline-flex items-center gap-1 text-xs text-emerald-300 hover:underline'
                >
                  <Globe size={12} />
                  {t('servers.openPublic', 'Otevřít ve Safari / prohlížeči')}
                </a>
                <button
                  type='button'
                  className={`text-xs ${copiedId === 'public' ? 'text-emerald-400' : 'text-gray-400 hover:text-gray-200'}`}
                  onClick={() => copy('public', cursorPublicUrl)}
                >
                  {copyLabel('public', t('servers.copyUrl', 'Kopírovat URL'))}
                </button>
              </div>
            </div>
          )}
          {isProject && !projectDomainPublished && (
            <div className='border border-amber-500/25 bg-amber-950/10 rounded-lg p-3 mt-2 space-y-2'>
              <div className='text-[11px] text-amber-300 flex items-start gap-1.5'>
                <AlertTriangle size={12} className='shrink-0 mt-0.5' />
                {t(
                  'connect.projectMcpUnavailable',
                  'Projektová DEV /mcp cesta zatím není připravená. Node-wide ani interní fallback se pro projektový token nepoužije.',
                )}
              </div>
              <Button
                size='sm'
                variant='primary'
                icon={<Globe size={14} />}
                loading={publishProjectM.isPending}
                onClick={() => publishProjectM.mutate()}
              >
                {t('connect.reconcileProjectMcp', 'Zapnout / dorovnat MCP na DEV URL')}
              </Button>
            </div>
          )}
        </div>

        {!isProject && scopes.length > 0 && (
          <div>
            <div className='text-xs text-gray-500 mb-1'>{t('connect.yourScopes', 'Tvoje oprávnění')}</div>
            <div className='flex flex-wrap gap-1'>
              {scopes.map((s) => <Badge key={s} variant='blue'>{s}</Badge>)}
            </div>
          </div>
        )}

        {/* Freshly created token */}
        {newToken && (
          <div className='border border-emerald-500/40 bg-emerald-500/5 rounded-lg p-4 space-y-3'>
            <div className='flex items-center gap-2 text-emerald-300 font-medium text-sm'>
              <Check size={16} /> {t('connect.created', 'Token vytvořen — ulož ho teď, plaintext se už nezobrazí.')}
            </div>
            <div>
              <div className='text-xs text-gray-500 mb-1'>{t('connect.apiKey', 'API klíč')}</div>
              <div className='flex items-center gap-2'>
                <code className='flex-1 bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-xs text-emerald-300 font-mono break-all'>
                  {newToken.plaintext}
                </code>
                <Button
                  size='sm'
                  variant='ghost'
                  icon={copiedId === 'key' ? <Check size={14} /> : <Copy size={14} />}
                  onClick={() => copy('key', newToken.plaintext)}
                >
                  {copiedId === 'key' ? t('ui.copied', 'Zkopírováno') : null}
                </Button>
              </div>
            </div>
            {newToken.protocolNote && (
              <div className='text-[11px] text-blue-300 bg-blue-500/5 border border-blue-500/20 rounded-lg p-2'>
                {newToken.protocolNote}
              </div>
            )}
            <div>
              <div className='flex items-center justify-between mb-1'>
                <div className='text-xs text-gray-500'>
                  {idePack ? IDE_META[idePack.ide].label : t('connect.clientGeneric', 'Vlastní / cURL')} config
                </div>
                <button
                  onClick={() => copy('snippet', newToken.snippet)}
                  className={`text-xs flex items-center gap-1 ${
                    copiedId === 'snippet' ? 'text-emerald-400' : 'text-blue-400 hover:text-blue-300'
                  }`}
                >
                  {copiedId === 'snippet' ? <Check size={12} /> : <Copy size={12} />}
                  {copyLabel('snippet', t('connect.copy', 'Zkopírovat'))}
                </button>
              </div>
              <pre className='bg-gray-950 border border-gray-800 rounded p-2 text-[11px] text-gray-300 font-mono overflow-x-auto max-h-64'>
{newToken.snippet}
              </pre>
            </div>
            <div className='flex justify-end'>
              <Button
                size='sm'
                variant='secondary'
                onClick={() => {
                  setNewToken(null);
                  setIdePack(null);
                }}
              >
                {t('connect.done', 'Hotovo, uložil jsem')}
              </Button>
            </div>
          </div>
        )}

        {/* Existing tokens */}
        <section>
          <h4 className='text-xs uppercase font-bold text-gray-500 mb-2'>{t('connect.existing', 'Tvoje connect tokeny')}</h4>
          {tokensLoading && <div className='text-gray-500 text-sm'>{t('common.loading', 'Načítám…')}</div>}
          {tokens.length === 0 && !tokensLoading && (
            <div className='text-gray-500 text-sm italic'>{t('connect.noneYet', 'Zatím žádný osobní token.')}</div>
          )}
          <div className='space-y-1.5'>
            {tokens.map((tk) => (
              <div
                key={tk.id}
                className={`border rounded-lg p-2.5 flex items-center gap-2 text-sm ${
                  tk.enabled ? 'border-gray-800 bg-gray-950' : 'border-gray-800 bg-gray-950/50 opacity-60'
                }`}
              >
                <div className='flex-1 min-w-0'>
                  <div className='text-gray-200 truncate'>
                    {tk.label}
                    {tk.project && (
                      <span className='ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-purple-500/15 border border-purple-500/30 text-purple-300'>
                        {t('connect.projectScoped', 'projekt')}: {tk.project}
                      </span>
                    )}
                  </div>
                  <div className='text-[11px] text-gray-500 font-mono'>
                    {tk.key_preview}… · use={tk.use_count}
                    {tk.expires_at ? ` · expires ${new Date(tk.expires_at).toLocaleDateString()}` : ' · persistent'}
                    {tk.last_used
                      ? ` · ${t('connect.lastUsed', 'naposledy')} ${new Date(tk.last_used).toLocaleString()}`
                      : ` · ${t('connect.neverUsed', 'nikdy nepoužito')}`}
                  </div>
                </div>
                {tk.enabled
                  ? (
                    <button
                      onClick={async () => {
                        if (await themedConfirm(t('connect.revokeConfirm', 'Zrušit token "{{label}}"?', { label: tk.label }))) revokeM.mutate(tk.id);
                      }}
                      className='text-gray-500 hover:text-red-400'
                      title={t('connect.revokeToken', 'Zrušit token')}
                    >
                      <Trash2 size={14} />
                    </button>
                  )
                  : <span className='text-[10px] uppercase text-gray-600'>revoked</span>}
              </div>
            ))}
          </div>
        </section>

        {/* Create form */}
        <section className='border-t border-gray-800 pt-4'>
          <h4 className='text-xs uppercase font-bold text-gray-500 mb-3 flex items-center gap-2'>
            <KeyRound size={13} /> {t('connect.generate', 'Vygenerovat osobní token')}
          </h4>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3'>
            <div>
              <label className='block text-xs text-gray-500 mb-1'>{t('connect.labelField', 'Popis (volitelně)')}</label>
              <input
                className='w-full bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none'
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={t('connect.labelPlaceholder', 'např. notebook Filip')}
              />
            </div>
            <div>
              <label className='block text-xs text-gray-500 mb-1'>{t('connect.expiry', 'Expirace')}</label>
              <Select
                className='w-full'
                value={expiresDays ?? ''}
                onChange={(e) => setExpiresDays(e.target.value === '' ? null : Number(e.target.value))}
              >
                <option value=''>Persistent</option>
                <option value='30'>{t('connect.days30', '30 dní')}</option>
                <option value='90'>{t('connect.days90', '90 dní')}</option>
                <option value='365'>{t('connect.year1', '1 rok')}</option>
              </Select>
            </div>
          </div>
          <Button
            variant='primary'
            size='sm'
            icon={<Plus size={14} />}
            loading={createM.isPending}
            disabled={!connectReady}
            onClick={() => void onCreateManual()}
          >
            {t('connect.createBtn', 'Vytvořit token')}
          </Button>
        </section>
      </div>
  );

  if (embedded) return body;

  // Portal na body — sidebar má backdrop-filter, fixed by se jinak
  // kreslil jen uvnitř lišty („nabídka uvnitř levého panelu").
  return createPortal(
    <div
      className='acm-overlay fixed inset-0 z-[90] flex items-center justify-center p-4'
      onClick={onClose}
    >
      {body}
    </div>,
    document.body,
  );
}
